import React from 'react';
import { CandidateOpportunityUtils, CurrencyUtil } from '@axiom/utils';
import type {
  AhqSubmission,
  AhqSubmissionCandidateOpportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import type { CurrencyCodeKeyTypes } from '@axiom/const';
import { CurrencyMap } from '@axiom/const';
import { Flashy } from '@axiom/ui';

import { NumberUtil } from '../../utils/number-util';

import type { OpportunitySubmissionCandidateRateSchemaType } from './opportunity-submission-candidate-rate-schema';

export const OpportunitySubmissionCandidateRateUtil = {
  getConvertedProposedHourlyCompensation({
    exchangeRate,
    proposedHourlyComp,
  }: {
    exchangeRate: OpportunityCandidate['exchangeRate'];
    proposedHourlyComp?: number | null;
  }) {
    return proposedHourlyComp && exchangeRate
      ? proposedHourlyComp * exchangeRate
      : 0;
  },

  setCalculatedField({
    values,
    exchangeRate,
    productBurdenRateMultiple,
    candidateBonusPercentage,
  }: {
    values: Partial<OpportunitySubmissionCandidateRateSchemaType>;
    exchangeRate: OpportunityCandidate['exchangeRate'];
    productBurdenRateMultiple: OpportunityCandidate['productBurdenRateMultiple'];
    candidateBonusPercentage: OpportunityCandidate['candidateBonusPercentage'];
  }) {
    const proposedHourlyCompensation =
      values.proposedHourlyComp && exchangeRate
        ? OpportunitySubmissionCandidateRateUtil.getConvertedProposedHourlyCompensation(
            {
              proposedHourlyComp: values.proposedHourlyComp,
              exchangeRate,
            }
          )
        : undefined;

    switch (values.fieldToCalculate) {
      case 'proposedHourlyComp': {
        const v =
          CandidateOpportunityUtils.calculateProposedHourlyComp({
            proposedHourlyRate: values.proposedHourlyBillRate,
            proposedMarginTarget: values.proposedMargin,
            productBurdenRateMultiple,
            candidateBonusPercentage,
          }) ?? undefined;
        const proposedHourlyComp =
          v && exchangeRate ? Number((v / exchangeRate).toFixed(2)) : undefined;
        return {
          ...values,
          proposedHourlyComp,
        };
      }

      case 'proposedMargin': {
        return {
          ...values,
          proposedMargin:
            CandidateOpportunityUtils.calculateProposedMarginTarget({
              proposedHourlyRate: values.proposedHourlyBillRate,
              proposedHourlyCompensation,
              productBurdenRateMultiple,
              candidateBonusPercentage,
            }) || undefined,
        };
      }

      case 'proposedHourlyBillRate': {
        return {
          ...values,
          proposedHourlyBillRate:
            CandidateOpportunityUtils.calculateProposedHourlyRate({
              proposedMarginTarget: values.proposedMargin,
              proposedHourlyCompensation,
              productBurdenRateMultiple,
              candidateBonusPercentage,
            }) || undefined,
        };
      }

      default: {
        return values;
      }
    }
  },

  isCpqTargetsUnavailable({
    opportunityCandidate,
  }: {
    opportunityCandidate: OpportunityCandidate;
  }) {
    const {
      marginFloorPct,
      marginTargetPct,
      marginUpperPct,
      marginApprovalFloor,
      marginApprovalCeiling,
    } = opportunityCandidate;
    return (
      !marginFloorPct ||
      !marginTargetPct ||
      !marginUpperPct ||
      !marginApprovalFloor ||
      !marginApprovalCeiling
    );
  },

  isDisabledState({
    opportunityCandidate,
  }: {
    opportunityCandidate: OpportunityCandidate;
  }) {
    return (
      CandidateOpportunityUtils.baseHourlyUnavailable(opportunityCandidate) ||
      !opportunityCandidate.exchangeRate
    );
  },

  getDefaultInputs({
    isConversion,
    opportunityCandidate,
  }: {
    isConversion: boolean;
    opportunityCandidate: OpportunityCandidate;
  }): Partial<OpportunitySubmissionCandidateRateSchemaType> {
    const { exchangeRate, proposedHourlyCompCandidateCurrency } =
      opportunityCandidate;

    const isDisabledState = this.isDisabledState({ opportunityCandidate });

    if (isDisabledState) {
      return {
        displayBillingRate: 'hourly',
        fieldToCalculate: 'proposedHourlyBillRate',
        proposedHourlyComp: 0,
        proposedMargin: 0,
        proposedHourlyBillRate: 0,
      };
    }
    const proposedDefaults =
      CandidateOpportunityUtils.getDefaultProposedFromPosition(
        opportunityCandidate
      );

    const {
      proposedHourlyCompensation: defaultComp,
      proposedMarginTarget: defaultMargin,
      proposedHourlyRate: defaultBillingRate,
    } = proposedDefaults;

    const proposedHourlyComp = Number(
      (
        proposedHourlyCompCandidateCurrency ||
        (isConversion && exchangeRate
          ? defaultComp / exchangeRate
          : defaultComp)
      ).toFixed(2)
    );

    return {
      displayBillingRate: 'hourly',
      fieldToCalculate: 'proposedHourlyBillRate',
      proposedHourlyComp,
      proposedMargin: Number(defaultMargin.toFixed(2)),
      proposedHourlyBillRate: Number(defaultBillingRate.toFixed(2)),
    };
  },

  isConversion({
    submissionCurrency,
    candidateCurrency,
  }: {
    submissionCurrency: CurrencyCodeKeyTypes;
    candidateCurrency: CurrencyCodeKeyTypes;
  }) {
    return submissionCurrency !== candidateCurrency;
  },

  getProposedHourlyComp({
    isConversion,
    opportunityCandidate,
  }: {
    isConversion: boolean;
    opportunityCandidate: OpportunityCandidate;
  }) {
    const proposedDefaults =
      CandidateOpportunityUtils.getDefaultProposedFromPosition(
        opportunityCandidate
      );

    const {
      proposedHourlyCompensation,
      exchangeRate,
      proposedHourlyCompCandidateCurrency,
    } = opportunityCandidate;

    if (proposedHourlyCompCandidateCurrency) {
      return Number(proposedHourlyCompCandidateCurrency.toFixed(2));
    }

    if (proposedHourlyCompensation) {
      return Number(
        (isConversion && exchangeRate
          ? proposedHourlyCompensation / exchangeRate
          : proposedHourlyCompensation
        ).toFixed(2)
      );
    }

    return Number(
      (proposedDefaults.proposedHourlyCompensation || 0).toFixed(2)
    );
  },

  getTalentHourlyComp({
    opportunityCandidate,
  }: {
    opportunityCandidate: OpportunityCandidate;
  }) {
    const { baseHourlyCompensation, exchangeRate } = opportunityCandidate;
    return baseHourlyCompensation && exchangeRate
      ? baseHourlyCompensation / exchangeRate
      : 0;
  },

  getTalentHourlyCompTooltipCopy({
    candidateCurrency,
    opportunityCandidate,
    hourlyComp,
  }: {
    candidateCurrency: CurrencyCodeKeyTypes;
    opportunityCandidate: OpportunityCandidate;
    hourlyComp: number;
  }) {
    return `How it's calculated: Salary (${CurrencyMap.currencySymbols[candidateCurrency]} - ${candidateCurrency} ${CurrencyUtil.getFormattedCurrency({ value: opportunityCandidate.candidateBaseAnnualSalary, currencyCode: candidateCurrency, removeCentsIfZero: false })}) / Work Schedule (${
      opportunityCandidate.candidateWorkscheduleAnnualHours
    }) * Premium Multiple (${NumberUtil.formatNumberAsDecimal(
      opportunityCandidate.productPremiumRateMultiple
    )}) = Talent Hourly Comp (${
      hourlyComp
        ? `${CurrencyMap.currencySymbols[candidateCurrency]} - ${candidateCurrency} ${CurrencyUtil.getFormattedCurrency(
            {
              value: hourlyComp,
              currencyCode: candidateCurrency,
              removeCentsIfZero: false,
            }
          )}`
        : 'Unavailable'
    })`;
  },

  getProposedHourlyCompTooltipCopy({
    candidateCurrency,
    opportunityCandidate,
    proposedHourlyComp,
    submissionCurrency,
  }: {
    candidateCurrency: CurrencyCodeKeyTypes;
    opportunityCandidate: OpportunityCandidate;
    proposedHourlyComp: number | null | undefined;
    submissionCurrency: CurrencyCodeKeyTypes;
  }) {
    const proposedHourlyCompensation = proposedHourlyComp ?? 0;
    const productBurdenRateMultiple =
      opportunityCandidate.productBurdenRateMultiple ?? 0;
    const exchangeRate = opportunityCandidate.exchangeRate ?? 0;
    const ebillingMandateMultiplePercentage =
      opportunityCandidate.ebillingMandateMultiple ?? 0;

    const hourlyOTE = proposedHourlyCompensation;
    const ebillingMultiple = ebillingMandateMultiplePercentage / 100 + 1;
    const totalHourlyCost =
      hourlyOTE *
      productBurdenRateMultiple *
      exchangeRate *
      (ebillingMandateMultiplePercentage > 1 ? ebillingMultiple : 1);

    return `How this is used: Proposed Hourly Comp (${CurrencyMap.currencySymbols[candidateCurrency]} - ${candidateCurrency} ${CurrencyUtil.getFormattedCurrency(
      {
        value: proposedHourlyCompensation,
        currencyCode: candidateCurrency,
        removeCentsIfZero: false,
      }
    )}) = Hourly OTE (${CurrencyMap.currencySymbols[candidateCurrency]} - ${candidateCurrency} ${CurrencyUtil.getFormattedCurrency(
      {
        value: hourlyOTE,
        currencyCode: candidateCurrency,
        removeCentsIfZero: false,
      }
    )}) * Burden Multiple (${NumberUtil.formatNumberAsDecimal(
      productBurdenRateMultiple
    )}) * Exchange Rate (${NumberUtil.formatNumberAsDecimal(exchangeRate)})${
      ebillingMandateMultiplePercentage > 1
        ? ` * EBilling Mandate Multiple (${NumberUtil.formatNumberAsDecimal(ebillingMultiple, 4)})`
        : ''
    } = Axiom’s Total Hourly Cost (${CurrencyMap.currencySymbols[submissionCurrency]} - ${submissionCurrency} ${CurrencyUtil.getFormattedCurrency(
      {
        value: totalHourlyCost,
        currencyCode: candidateCurrency,
        removeCentsIfZero: false,
      }
    )})`;
  },

  getConvertedToCurrencyTooltip({
    candidateCurrency,
    convertedComp,
    exchangeRate,
    proposedHourlyComp,
    submissionCurrency,
  }: {
    candidateCurrency: CurrencyCodeKeyTypes;
    convertedComp: string;
    exchangeRate: NonNullable<OpportunityCandidate['exchangeRate']>;
    proposedHourlyComp: number;
    submissionCurrency: CurrencyCodeKeyTypes;
  }) {
    const formattedProposedHourlyComp =
      CurrencyUtil.getFormattedCurrency({
        value: proposedHourlyComp,
        currencyCode: candidateCurrency,
        removeCentsIfZero: false,
      }) ?? '0.00';
    const numericConvertedComp = Number(convertedComp);
    const formattedConvertedComp = Number.isFinite(numericConvertedComp)
      ? (CurrencyUtil.getFormattedCurrency({
          value: numericConvertedComp,
          currencyCode: candidateCurrency,
          removeCentsIfZero: false,
        }) ?? '-')
      : '-';
    return `How it's calculated: Proposed Hourly Comp ${''}
    (${CurrencyMap.currencySymbols[candidateCurrency]} - ${candidateCurrency} ${formattedProposedHourlyComp}) ${''}
    * Exchange Rate (${NumberUtil.formatNumberAsDecimal(exchangeRate)}) ${''}
    = Converted Proposed Hourly Comp (${CurrencyMap.currencySymbols[submissionCurrency]} - ${submissionCurrency} ${formattedConvertedComp})`;
  },

  isResetDisabled({
    formValues,
    opportunityCandidate,
  }: {
    formValues: Partial<OpportunitySubmissionCandidateRateSchemaType>;
    opportunityCandidate: OpportunityCandidate;
  }) {
    const proposedDefaults =
      CandidateOpportunityUtils.getDefaultProposedFromPosition(
        opportunityCandidate
      );

    return (
      (proposedDefaults.proposedHourlyCompensation ===
        formValues.proposedHourlyComp &&
        proposedDefaults.proposedMarginTarget === formValues.proposedMargin &&
        proposedDefaults.proposedHourlyRate ===
          formValues.proposedHourlyBillRate) ||
      this.isCpqTargetsUnavailable({ opportunityCandidate })
    );
  },

  getCurrencyLabel({
    currency,
    isConversion,
    name,
  }: {
    currency: CurrencyCodeKeyTypes;
    isConversion?: boolean;
    name: string;
  }) {
    return React.createElement(
      Flashy,
      { name, bold: true, color: isConversion ? 'dataVizRed' : 'textDefault' },
      '(',
      CurrencyMap.currencySymbols[currency],
      ' - ',
      currency,
      ')'
    );
  },

  formatBillingRate({ rate }: { rate: number | null | undefined }): string {
    return rate?.toFixed(2) ?? '0.00';
  },

  formatBillingRateWithHours({
    rate,
    hoursPerDay,
  }: {
    rate: number | null | undefined;
    hoursPerDay: number | null | undefined;
  }): string {
    return rate && hoursPerDay
      ? OpportunitySubmissionCandidateRateUtil.formatBillingRate({
          rate: rate * hoursPerDay,
        })
      : OpportunitySubmissionCandidateRateUtil.formatBillingRate({
          rate: null,
        });
  },

  formatDisplayRate({
    opportunityCandidate,
    submission,
  }: {
    opportunityCandidate: OpportunityCandidate;
    submission: AhqSubmission;
  }): string | null {
    switch (opportunityCandidate.displayBillingRate) {
      case 'hourly': {
        return `${CurrencyUtil.getFormattedCurrency({
          value: OpportunitySubmissionCandidateRateUtil.formatBillingRate({
            rate: opportunityCandidate.proposedHourlyRate,
          }),
          currencyCode: submission.currency,
        })}/hr`;
      }

      case 'daily': {
        return `${CurrencyUtil.getFormattedCurrency({
          value:
            OpportunitySubmissionCandidateRateUtil.formatBillingRateWithHours({
              rate: opportunityCandidate.proposedHourlyRate,
              hoursPerDay: opportunityCandidate.hoursPerDay,
            }),
          currencyCode: submission.currency,
        })}/day`;
      }

      case 'weekly': {
        return `${CurrencyUtil.getFormattedCurrency({
          value:
            opportunityCandidate.hoursPerDay === null
              ? OpportunitySubmissionCandidateRateUtil.formatBillingRateWithHours(
                  {
                    rate: opportunityCandidate.proposedHourlyRate,
                    hoursPerDay: opportunityCandidate.hoursPerDay,
                  }
                )
              : OpportunitySubmissionCandidateRateUtil.formatBillingRateWithHours(
                  {
                    rate: opportunityCandidate.proposedHourlyRate,
                    hoursPerDay: (opportunityCandidate.hoursPerDay ?? 0) * 5,
                  }
                ),
          currencyCode: submission.currency,
        })}/wk`;
      }

      default: {
        return null;
      }
    }
  },

  getCurrencyOrDefault({
    currency,
  }: {
    currency: string | null | undefined;
  }): CurrencyCodeKeyTypes {
    return (currency ?? CurrencyMap.codes.USD) as CurrencyCodeKeyTypes;
  },

  getCpqIndicator({
    formValues,
    marginApprovalFloor,
    marginApprovalCeiling,
  }: {
    formValues: Partial<OpportunitySubmissionCandidateRateSchemaType>;
    marginApprovalFloor: NonNullable<
      AhqSubmissionCandidateOpportunity['marginApprovalFloor']
    >;
    marginApprovalCeiling: NonNullable<
      AhqSubmissionCandidateOpportunity['marginApprovalCeiling']
    >;
  }) {
    let color: 'dataVizYellow' | 'dataVizForestGreen' = 'dataVizYellow';
    let iconName: 'alert-filled' | 'circle-check-filled' = 'alert-filled';
    let message = `Proposed margin is below the approval threshold of ${marginApprovalFloor}%. Please follow the approval process for review and be ready to explain why you are requesting the lower margin. Additionally, please consider using lower cost talent options (e.g. Lower ESC) to help protect gross margin and/or offer a more competitive price.`;
    let testId = 'cpq-indicator-below-threshold';

    if (
      formValues.proposedMargin !== null &&
      formValues.proposedMargin !== undefined &&
      formValues.proposedMargin > marginApprovalCeiling
    ) {
      color = 'dataVizYellow';
      iconName = 'alert-filled';
      message = `Proposed margin is above the approval threshold of ${marginApprovalCeiling}%. Please follow the approval process for review and be ready to explain why you are requesting the higher margin.`;
      testId = 'cpq-indicator-above-threshold';
    } else if (
      formValues.proposedMargin !== null &&
      formValues.proposedMargin !== undefined &&
      formValues.proposedMargin >= marginApprovalFloor
    ) {
      color = 'dataVizForestGreen';
      iconName = 'circle-check-filled';
      message = `Within target margin range. Approval not required above ${marginApprovalFloor}%.`;
      testId = 'cpq-indicator-within-threshold';
    }

    return {
      color,
      iconName,
      message,
      testId,
    };
  },
};
